import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Megaphone,
  Plus,
  Edit,
  Trash2,
  Send,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Eye,
  EyeOff,
  Bell
} from 'lucide-react';

const AnnouncementsPage = () => {
  const { user, API_URL, getAuthHeader } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [bakeries, setBakeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    target: 'all',
    target_bakeries: [],
    priority: 'normal',
    expires_at: ''
  });

  // Fetch announcements
  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/superadmin/announcements`, {
        headers: getAuthHeader()
      });
      setAnnouncements(res.data);
    } catch (err) {
      console.error('Error fetching announcements:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch bakeries for targeting
  const fetchBakeries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/all-bakeries`, {
        headers: getAuthHeader()
      });
      setBakeries(res.data.filter(b => !b.is_super_admin));
    } catch (err) {
      console.error('Error fetching bakeries:', err);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    fetchBakeries();
  }, []);

  // Open dialog
  const openDialog = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setFormData({
        title: announcement.title,
        message: announcement.message,
        target: announcement.target,
        target_bakeries: announcement.target_bakeries || [],
        priority: announcement.priority,
        expires_at: announcement.expires_at || ''
      });
    } else {
      setEditingAnnouncement(null);
      setFormData({
        title: '',
        message: '',
        target: 'all',
        target_bakeries: [],
        priority: 'normal',
        expires_at: ''
      });
    }
    setDialogOpen(true);
  };

  // Save announcement
  const saveAnnouncement = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('العنوان والرسالة مطلوبان');
      return;
    }

    setSaving(true);
    try {
      if (editingAnnouncement) {
        await axios.put(
          `${API_URL}/api/announcements/${editingAnnouncement.id}`,
          { title: formData.title, message: formData.message },
          { headers: getAuthHeader() }
        );
        toast.success('تم تحديث الإعلان');
      } else {
        const res = await axios.post(
          `${API_URL}/api/announcements`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success(`تم إرسال الإعلان إلى ${res.data.notified_count} مخبز`);
      }
      
      setDialogOpen(false);
      fetchAnnouncements();
    } catch (err) {
      toast.error('فشل حفظ الإعلان');
    } finally {
      setSaving(false);
    }
  };

  // Toggle announcement active status
  const toggleActive = async (announcement) => {
    try {
      await axios.put(
        `${API_URL}/api/announcements/${announcement.id}`,
        { is_active: !announcement.is_active },
        { headers: getAuthHeader() }
      );
      toast.success(announcement.is_active ? 'تم إخفاء الإعلان' : 'تم تفعيل الإعلان');
      fetchAnnouncements();
    } catch (err) {
      toast.error('فشل تحديث الإعلان');
    }
  };

  // Delete announcement
  const deleteAnnouncement = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الإعلان؟')) return;
    
    try {
      await axios.delete(`${API_URL}/api/announcements/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('تم حذف الإعلان');
      fetchAnnouncements();
    } catch (err) {
      toast.error('فشل حذف الإعلان');
    }
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'urgent':
        return <Badge className="bg-red-500 text-white gap-1"><AlertTriangle className="w-3 h-3" /> عاجل</Badge>;
      case 'important':
        return <Badge className="bg-orange-500 text-white gap-1"><Bell className="w-3 h-3" /> مهم</Badge>;
      default:
        return <Badge variant="outline" className="text-stone-500">عادي</Badge>;
    }
  };

  // Get target text
  const getTargetText = (announcement) => {
    if (announcement.target === 'all') {
      return 'جميع المخابز';
    } else if (announcement.target === 'specific' && announcement.target_bakeries?.length > 0) {
      return `${announcement.target_bakeries.length} مخبز محدد`;
    } else {
      const bakery = bakeries.find(b => b.id === announcement.target);
      return bakery?.name || 'مخبز محدد';
    }
  };

  if (!user?.is_super_admin) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-stone-500">هذه الصفحة متاحة فقط للمدير الرئيسي</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <Megaphone className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-800">الإعلانات والإشعارات</h1>
            <p className="text-stone-500">إرسال رسائل وإشعارات لأصحاب المخابز</p>
          </div>
        </div>
        
        <Button onClick={() => openDialog()} className="gap-2 bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4" />
          إعلان جديد
        </Button>
      </div>

      {/* Announcements List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-purple-600" />
            الإعلانات المرسلة
            {announcements.length > 0 && (
              <Badge variant="secondary">{announcements.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-12 text-stone-400">
              <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>لم يتم إرسال أي إعلانات بعد</p>
              <p className="text-sm mt-1">انقر "إعلان جديد" لإرسال أول إعلان</p>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((announcement) => (
                <div 
                  key={announcement.id}
                  className={`p-4 border rounded-lg ${
                    !announcement.is_active ? 'bg-stone-50 opacity-60' : 
                    announcement.priority === 'urgent' ? 'bg-red-50 border-red-200' :
                    announcement.priority === 'important' ? 'bg-orange-50 border-orange-200' :
                    'bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-stone-800">{announcement.title}</h3>
                        {getPriorityBadge(announcement.priority)}
                        {!announcement.is_active && (
                          <Badge variant="outline" className="text-stone-400">مخفي</Badge>
                        )}
                      </div>
                      
                      <p className="text-stone-600 whitespace-pre-wrap mb-3">{announcement.message}</p>
                      
                      <div className="flex items-center gap-4 text-sm text-stone-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {getTargetText(announcement)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(announcement.created_at).toLocaleDateString('ar-EG', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleActive(announcement)}
                        title={announcement.is_active ? 'إخفاء' : 'إظهار'}
                      >
                        {announcement.is_active ? (
                          <EyeOff className="w-4 h-4 text-stone-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-green-600" />
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openDialog(announcement)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600"
                        onClick={() => deleteAnnouncement(announcement.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-purple-600" />
              {editingAnnouncement ? 'تعديل الإعلان' : 'إعلان جديد'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>العنوان</Label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="عنوان الإعلان..."
              />
            </div>
            
            <div className="space-y-2">
              <Label>الرسالة</Label>
              <Textarea 
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="نص الإعلان..."
                rows={5}
              />
            </div>
            
            {!editingAnnouncement && (
              <>
                <div className="space-y-2">
                  <Label>الأولوية</Label>
                  <Select 
                    value={formData.priority}
                    onValueChange={(val) => setFormData({ ...formData, priority: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">عادي</SelectItem>
                      <SelectItem value="important">مهم</SelectItem>
                      <SelectItem value="urgent">عاجل</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>إرسال إلى</Label>
                  <Select 
                    value={formData.target}
                    onValueChange={(val) => setFormData({ ...formData, target: val, target_bakeries: [] })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">جميع المخابز</SelectItem>
                      <SelectItem value="specific">مخابز محددة</SelectItem>
                      {bakeries.map(bakery => (
                        <SelectItem key={bakery.id} value={bakery.id}>
                          {bakery.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.target === 'specific' && (
                  <div className="space-y-2">
                    <Label>اختر المخابز</Label>
                    <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-2">
                      {bakeries.map(bakery => (
                        <label key={bakery.id} className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.target_bakeries.includes(bakery.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  target_bakeries: [...formData.target_bakeries, bakery.id]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  target_bakeries: formData.target_bakeries.filter(id => id !== bakery.id)
                                });
                              }
                            }}
                            className="rounded"
                          />
                          <span>{bakery.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              إلغاء
            </Button>
            <Button 
              onClick={saveAnnouncement} 
              disabled={saving}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {editingAnnouncement ? 'تحديث' : 'إرسال الإعلان'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnouncementsPage;
